import React, { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Spinner,
  Tabs,
} from "../../design-system";
import PortalLayout from "./PortalLayout";
import useInspectionPortal from "./useInspectionPortal";
import { formatInstant, formatTimeHHMM } from "../../helpers/datetime";

export default function PortalVerificar() {
  document.title = "Inspección · Verificar export | Kronaly";
  return (
    <PortalLayout>
      <Body />
    </PortalLayout>
  );
}

const TABS = [
  { id: "id", label: "Por Signature ID (visual)" },
  { id: "file", label: "Por archivo (criptográfica)" },
];

function Body() {
  const [active, setActive] = useState("id");

  return (
    <>
      <PageHeader
        title="Verificar export firmado"
        description="Tienes dos vías complementarias: comparar visualmente los datos contra lo que tienes en la mano, o verificar la integridad criptográfica del archivo descargado."
      />

      <Tabs tabs={TABS} value={active} onChange={setActive} />

      <div style={{ marginTop: 16 }}>
        {active === "id" ? <ById /> : <ByFile />}
      </div>
    </>
  );
}

// ───────────────── Vista 1: por Signature ID (visual) ─────────────────

function ById() {
  const { http, workspace } = useInspectionPortal();
  const tz = workspace?.timezone;
  const [signatureId, setSignatureId] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const verify = async (e) => {
    e?.preventDefault?.();
    if (!signatureId.trim()) return;
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const r = await http.get(`/inspection/exports/${encodeURIComponent(signatureId.trim())}/verify`);
      setResult(r?.data?.data || r?.data || null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        setError("La firma no existe o no pertenece al workspace de tu token.");
      } else {
        setError(err?.response?.data?.error?.message || err?.message || "No se pudo verificar.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Alert tone="info">
        <strong>Cómo funciona:</strong> introduce el Signature ID que aparece en el manifest del
        archivo que tienes (PDF, CSV o JSON). El sistema te muestra los datos que estaban en el
        export original (reconstruidos desde la base de datos). Compara visualmente con tu archivo
        para detectar si algo ha cambiado. Útil cuando tu archivo es PDF y prefieres una validación
        humana antes que una criptográfica.
      </Alert>

      <Card>
        <form onSubmit={verify} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Field label="Signature ID" required description="UUID del manifest, p.ej. 0190a3c2-…-…-…-…">
            <Input
              value={signatureId}
              onChange={(e) => setSignatureId(e.target.value)}
              placeholder="0190a3c2-…"
              autoFocus
              autoComplete="off"
            />
          </Field>

          <div>
            <Button type="submit" variant="primary" icon="Eye" disabled={!signatureId.trim() || loading}>
              {loading ? "Cargando…" : "Mostrar registros"}
            </Button>
          </div>
        </form>
      </Card>

      {error && (
        <div style={{ marginTop: 16 }}>
          <Alert tone="danger">{error}</Alert>
        </div>
      )}

      {result && (
        <div style={{ marginTop: 16 }}>
          <ByIdResult result={result} tz={tz} />
        </div>
      )}
    </>
  );
}

function ByIdResult({ result, tz }) {
  const m = result.manifest || {};
  const records = result.records || [];
  const serverUnchanged = !!result.server_data_unchanged;
  const manifestValid = !!result.valid;

  return (
    <>
      <Card>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          {manifestValid ? (
            <Badge tone="success" dot>Manifest válido</Badge>
          ) : (
            <Badge tone="danger" dot>Manifest alterado en BD</Badge>
          )}
          {serverUnchanged ? (
            <Badge tone="success" dot>Datos del servidor sin cambios</Badge>
          ) : (
            <Badge tone="warning" dot>Datos del servidor han cambiado</Badge>
          )}
        </div>

        {!serverUnchanged && (
          <Alert tone="warning">
            Los datos actuales del servidor para este rango ya no coinciden con el hash del export
            original. Pueden haberse aplicado correcciones aprobadas, soft-deletes u operaciones de
            mantenimiento desde la generación. Si esto te sorprende, contacta con el responsable y
            consulta la pestaña Auditoría.
          </Alert>
        )}

        <div style={metaGrid}>
          <Meta label="Signature ID" value={m.signature_id} mono />
          <Meta label="Generado" value={formatDateTime(m.generated_at, tz)} />
          <Meta label="Workspace" value={m.workspace_id} mono />
          <Meta label="Rango" value={m.range ? `${m.range.from} → ${m.range.to}` : "—"} />
          <Meta label="Formato original" value={(m.format || "—").toUpperCase()} />
          <Meta label="Filas en el export" value={m.rows_count != null ? String(m.rows_count) : "—"} />
          <Meta label="Hash contenido (original)" value={m.content_hash_sha256} mono />
          <Meta label="Hash contenido (actual)" value={result.current_content_hash_sha256} mono />
        </div>

        {result.query_filters && (
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--kr-fg-muted)" }}>
            Filtros aplicados al export: <code>{JSON.stringify(result.query_filters)}</code>
          </div>
        )}
      </Card>

      <div style={{ marginTop: 16 }}>
        <h3 style={{ fontSize: 14, marginBottom: 8 }}>
          Registros que debían estar en el archivo ({records.length})
        </h3>
        <RecordsTable records={records} tz={tz} />
      </div>
    </>
  );
}

// ───────────────── Vista 2: por archivo (criptográfica) ─────────────────

function ByFile() {
  const { http, workspace } = useInspectionPortal();
  const tz = workspace?.timezone;
  const [file, setFile] = useState(null);
  const [format, setFormat] = useState("json");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const onFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (f) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (ext === "csv") setFormat("csv");
      else if (ext === "json") setFormat("json");
      else if (ext === "pdf") setFormat("pdf");
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (!file) return;
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("format", format);
      fd.append("file", file);
      const r = await http.post("/inspection/exports/verify-file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r?.data?.data || r?.data || null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 422) {
        setError(err?.response?.data?.error?.message || "Archivo no válido o no parseable.");
      } else if (status === 403) {
        setError(err?.response?.data?.error?.message || "El archivo pertenece a otro workspace.");
      } else {
        setError(err?.response?.data?.error?.message || err?.message || "No se pudo verificar.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Alert tone="info">
        <strong>Cómo funciona:</strong> sube el archivo descargado (PDF, CSV o JSON). El sistema
        realiza hasta tres comprobaciones independientes para confirmar si el archivo es auténtico
        y los datos del servidor siguen siendo los mismos.
        <br /><br />
        <strong>Todos los formatos están soportados.</strong> El PDF incluye en su interior un
        bloque oculto con el contenido firmado que el verificador puede leer, igual que con CSV o JSON.
      </Alert>

      <Card>
        <form onSubmit={verify} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Field label="Archivo descargado" required>
            <input
              type="file"
              accept=".pdf,.csv,.json,application/pdf,application/json,text/csv"
              onChange={onFileChange}
              style={fileInput}
            />
            {file && (
              <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 4 }}>
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </div>
            )}
          </Field>

          <Field label="Formato" description="Se detecta automáticamente por la extensión, pero puedes forzarlo si la extensión no coincide.">
            <select value={format} onChange={(e) => setFormat(e.target.value)} style={selectStyle}>
              <option value="pdf">PDF</option>
              <option value="json">JSON</option>
              <option value="csv">CSV</option>
            </select>
          </Field>

          <div>
            <Button type="submit" variant="primary" icon="ShieldCheck" disabled={!file || loading}>
              {loading ? "Verificando…" : "Verificar archivo"}
            </Button>
          </div>
        </form>
      </Card>

      {error && (
        <div style={{ marginTop: 16 }}>
          <Alert tone="danger">{error}</Alert>
        </div>
      )}

      {result && (
        <div style={{ marginTop: 16 }}>
          <ByFileResult result={result} tz={tz} />
        </div>
      )}
    </>
  );
}

function ByFileResult({ result, tz }) {
  const m = result.manifest || {};
  const c = result.checks || {};
  const allOk =
    c.manifest_signature_valid === true &&
    (c.file_content_unchanged === true || c.file_content_unchanged === null) &&
    c.server_data_unchanged === true;

  return (
    <>
      <Card>
        <div style={{ marginBottom: 16 }}>
          {allOk ? (
            <Badge tone="success" dot>Archivo auténtico y datos del servidor sin cambios</Badge>
          ) : (
            <Badge tone="danger" dot>Verificación fallida (ver detalle abajo)</Badge>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          <CheckRow
            label="El manifest dentro del archivo no fue manipulado"
            value={c.manifest_signature_valid}
            help="HMAC del manifest del archivo vs. HMAC recalculada con la clave del servidor."
          />
          <CheckRow
            label="El contenido del archivo coincide con su manifest"
            value={c.file_content_unchanged}
            help="Hash SHA-256 del contenido tabular del archivo vs. content_hash del manifest. PDF y JSON sí permiten esta comprobación porque incluyen el contenido canónico completo; CSV no (estructura no reconstruible 1:1)."
          />
          <CheckRow
            label="Los datos del servidor no han cambiado desde la generación"
            value={c.server_data_unchanged}
            help="content_hash recalculado sobre los datos actuales del servidor vs. content_hash del manifest del archivo."
          />
        </div>

        <div style={metaGrid}>
          <Meta label="Signature ID" value={m.signature_id} mono />
          <Meta label="Generado" value={formatDateTime(m.generated_at, tz)} />
          <Meta label="Rango" value={m.range ? `${m.range.from} → ${m.range.to}` : "—"} />
          <Meta label="Formato del archivo subido" value={(m.format || "—").toUpperCase()} />
          <Meta label="Hash contenido (manifest)" value={m.content_hash_sha256} mono />
          <Meta label="Hash contenido (servidor actual)" value={result.current_content_hash_sha256} mono />
          {result.file_content_hash_sha256 && (
            <Meta label="Hash contenido (archivo subido)" value={result.file_content_hash_sha256} mono />
          )}
        </div>

        {result.signature_persisted === false && (
          <Alert tone="warning">
            <strong>Aviso:</strong> no encontramos esta signature en la base de datos del servidor.
            El archivo puede ser válido criptográficamente, pero el servidor no recuerda haberla
            emitido (podría haber sido purgada o pertenecer a un entorno distinto).
          </Alert>
        )}
      </Card>

      {result.records_current && result.records_current.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 14, marginBottom: 8 }}>
            Registros actuales en el servidor para el mismo rango ({result.records_current.length})
          </h3>
          <RecordsTable records={result.records_current} tz={tz} />
        </div>
      )}
    </>
  );
}

function CheckRow({ label, value, help }) {
  let tone;
  let icon;
  if (value === true) { tone = "success"; icon = "✓"; }
  else if (value === false) { tone = "danger"; icon = "✗"; }
  else { tone = "neutral"; icon = "—"; }

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
      <div style={{
        ...checkBadge,
        background: tone === "success" ? "#d1fae5" : tone === "danger" ? "#fee2e2" : "#e5e7eb",
        color: tone === "success" ? "#065f46" : tone === "danger" ? "#991b1b" : "#4b5563",
      }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kr-fg-strong)" }}>{label}</div>
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 2 }}>
          {value === null ? "No aplicable a este formato. " : ""}{help}
        </div>
      </div>
    </div>
  );
}

// ───────────────── Compartidos ─────────────────

function RecordsTable({ records, tz }) {
  if (records.length === 0) {
    return (
      <Card>
        <div style={{ padding: 12, textAlign: "center", color: "var(--kr-fg-muted)" }}>
          No hay registros en este rango.
        </div>
      </Card>
    );
  }

  return (
    <Card style={{ padding: 0, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>Fecha</th>
            <th style={th}>Empleado</th>
            <th style={th}>Estado</th>
            <th style={th}>Entradas / Salidas</th>
            <th style={th}>Pausas</th>
          </tr>
        </thead>
        <tbody>
          {records.map((w) => (
            <tr key={w.workday_id} style={!w.is_active ? { background: "var(--kr-bg-subtle)" } : undefined}>
              <td style={td}>{w.date}</td>
              <td style={td}>{w.employee_name || "—"}</td>
              <td style={td}>
                {w.status}
                {!w.is_active && (
                  <div style={{ fontSize: 10, color: "var(--kr-danger-700, #b91c1c)" }}>eliminada</div>
                )}
              </td>
              <td style={td}>
                {w.segments.length === 0 ? "—" : w.segments.map((s) => (
                  <div key={s.id} style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
                    {formatTime(s.clock_in_at, tz)} → {s.clock_out_at ? formatTime(s.clock_out_at, tz) : "en curso"}
                    {!s.is_active && <span style={{ color: "#b91c1c", marginLeft: 6 }}>(eliminado)</span>}
                  </div>
                ))}
              </td>
              <td style={td}>
                {w.breaks.length === 0 ? "—" : w.breaks.map((b, i) => (
                  <div key={b.id || i} style={{ fontFamily: "var(--kr-font-mono)", fontSize: 12 }}>
                    {formatTime(b.start_at, tz)} – {formatTime(b.end_at, tz)}
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div style={metaLabel}>{label}</div>
      <div style={{ ...metaValue, ...(mono ? monoStyle : {}) }}>{value || "—"}</div>
    </div>
  );
}

// Instantes en la zona del workspace de inspección (tz se hereda por props).
function formatDateTime(iso, tz) {
  return formatInstant(iso, tz);
}

function formatTime(iso, tz) {
  return formatTimeHHMM(iso, tz);
}

const metaGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 16,
};

const metaLabel = {
  fontSize: 11,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
  marginBottom: 4,
};

const metaValue = {
  fontSize: 14,
  color: "var(--kr-fg-strong)",
};

const monoStyle = {
  fontFamily: "var(--kr-font-mono)",
  fontSize: 12,
  wordBreak: "break-all",
};

const th = {
  textAlign: "left",
  padding: "10px 12px",
  borderBottom: "1px solid var(--kr-border)",
  background: "var(--kr-bg-subtle)",
  fontSize: 11,
  textTransform: "uppercase",
  color: "var(--kr-fg-muted)",
  fontWeight: 600,
};

const td = {
  padding: "10px 12px",
  borderBottom: "1px solid var(--kr-border-subtle)",
  verticalAlign: "top",
  fontSize: 12,
};

const fileInput = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid var(--kr-border)",
  background: "var(--kr-bg)",
  fontSize: 14,
  width: "100%",
};

const selectStyle = {
  padding: "8px 12px",
  borderRadius: 6,
  border: "1px solid var(--kr-border)",
  background: "var(--kr-bg)",
  color: "var(--kr-fg)",
  fontSize: 14,
  minWidth: 160,
};

const checkBadge = {
  width: 24,
  height: 24,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: 14,
  flexShrink: 0,
};
